package one.healthium.update_food_preferences.ui

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import loco.HEALTH_LOG_SAVE_BUTTON
import loco.UPDATE_FOOD_PREFERENCES_SCREEN_TITLE
import one.healthium.ui.common.event_producer.EventEffect
import one.healthium.ui.common.message.AlertUiMessageHandler
import one.healthium.ui.components.question.AnswerUiModel
import one.healthium.ui.components.question.QuestionItem
import one.healthium.ui.components.question.QuestionUiModel
import one.healthium.ui.compose.components.AppLoadingScreen
import one.healthium.ui.compose.components.AppProgressButton
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.components.AppSheetTopBar
import one.healthium.ui.compose.theme.AppTheme
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

interface UpdateFoodPreferencesEventDelegate {
    fun onCloseClicked()
}

@Composable
fun UpdateFoodPreferencesScreen(eventDelegate: UpdateFoodPreferencesEventDelegate) {
    val viewModel = koinViewModel<UpdateFoodPreferencesViewModel>()
    val state by viewModel.state.collectAsState()

    EventEffect(viewModel.events) { event ->
        when (event) {
            UpdateFoodPreferencesEvent.OnFoodPreferencesSaved -> eventDelegate.onCloseClicked()
        }
    }
    AlertUiMessageHandler(viewModel.uiMessage)

    UpdateFoodPreferencesScreenContent(
        state = state,
        onCloseClicked = eventDelegate::onCloseClicked,
        onSaveClicked = viewModel::onSaveClicked,
        onAnswerSelected = viewModel::onAnswerSelected,
    )
}

@Composable
private fun UpdateFoodPreferencesScreenContent(
    state: UpdateFoodPreferencesUiState,
    onCloseClicked: () -> Unit,
    onSaveClicked: () -> Unit,
    onAnswerSelected: (QuestionUiModel, AnswerUiModel) -> Unit,
) {
    AppScaffold(
        topBar = {
            AppSheetTopBar(onCloseClicked = onCloseClicked)
        },
    ) {
        Box(Modifier.fillMaxSize()) {
            Column(
                modifier = Modifier
                    .padding(it)
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState())
                    .navigationBarsPadding(),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Spacer(Modifier.height(14.dp))
                Text(
                    modifier = Modifier.padding(horizontal = 32.dp),
                    text = stringResource(loco.Res.string.UPDATE_FOOD_PREFERENCES_SCREEN_TITLE),
                    style = AppTheme.logControlStyle?.titleStyle ?: AppTheme.textStyles.secondary_30_500,
                    color = AppTheme.colors.text100,
                    textAlign = TextAlign.Center,
                )
                Spacer(Modifier.height(12.dp))
                state.uiData?.forEach { question ->
                    QuestionItem(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 24.dp, vertical = 12.dp),
                        uiModel = question,
                        onAnswerSelected = { option ->
                            onAnswerSelected(question, option)
                        },
                    )
                }
                Spacer(Modifier.height(150.dp))
            }
            AppLoadingScreen(
                modifier = Modifier.fillMaxSize(),
                visible = state.loading || state.saveInProgress,
                dim = state.uiData != null,
            )
            AppProgressButton(
                modifier = Modifier
                    .defaultMinSize(minWidth = 200.dp)
                    .padding(bottom = 50.dp)
                    .navigationBarsPadding()
                    .align(Alignment.BottomCenter),
                onClick = onSaveClicked,
                loading = state.saveInProgress,
                text = stringResource(loco.Res.string.HEALTH_LOG_SAVE_BUTTON),
            )
        }
    }
}

@Composable
@Preview(showBackground = true)
fun UpdateFoodPreferencesScreenPreview() {
    AppTheme {
        UpdateFoodPreferencesScreenContent(
            state = UpdateFoodPreferencesUiState(loading = false),
            onCloseClicked = {},
            onSaveClicked = {},
            onAnswerSelected = { _, _ -> },
        )
    }
}
